import React, { useState, useEffect, useRef } from 'react';
import { View, BackHandler, StyleSheet, Text, Pressable } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { usePantryName } from '../usePantryName';
import { selectPantrySnapshot, selectTidyCount } from '../../../redux/pantrySelectors';
import { PantryImportBanner } from '../PantryImportBanner';
import { activeImportBatch } from '../importBatch';
import { DeliveryReview } from '../DeliveryReview';
import { useImportLink } from '../useImportLink';
import { addItemsAndSync, removeItemAndSync, markImportReviewedAndSync } from '../../../redux/inventorySlice';
import { PantryLocation } from '../../../utils/pantryLocation';
import { consumePendingPantryToast } from '../pendingToast';
import { usePantryToast, PantryToast } from '../PantryToast';
import { usePantryActions } from '../usePantryActions';
import { useClearPantry } from '../useClearPantry';
import { ClearPantryRow } from '../ClearPantryRow';
import { ConfirmationModal } from '../../common/ConfirmationModal';
import { trackEvent } from '../../../services/analyticsService';
import { buildDemoItems } from '../demoSeed';
import { EmptyState } from '../EmptyState';
import { PantryDrawer } from './PantryDrawer';
import { ItemPanel } from './ItemPanel';
import { AddPanel } from './AddPanel';
import { CookStrip } from '../CookStrip';
import { DesktopZoneColumn } from './DesktopZoneColumn';
import { DragGhost } from './DragGhost';
import { usePantryDrag } from './usePantryDrag';
import { CookPanel } from './CookPanel';
import { RecipesPanel } from './RecipesPanel';
import { ReviewPanel } from './ReviewPanel';
import { DedupePanel } from './DedupePanel';
import { PantryIcon } from '../PantryIcon';
import { Paprika, PaprikaTypography } from '../../../styles/theme';
import { detectCategoryFromName } from '../../../utils/inventoryUtils';
import { estimateExpiry } from '../../../services/pantryFreshnessService';
import { RootState, AppDispatch } from '../../../redux/store';
import type { PantryDrawerState } from '../types';
import { ZONE_KEYS } from '../zoneMeta';
import { useScanGate } from '../../auth/ScanSignUp';

export function PantryDesktop() {
  const router = useRouter();
  const snapshot = useSelector(selectPantrySnapshot);
  const tidyCount = useSelector(selectTidyCount);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const pantryName = usePantryName();
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const [drawer, setDrawer] = useState<PantryDrawerState>(null);
  const [justMovedItemId, setJustMovedItemId] = useState<string | null>(null);
  const containerRef = useRef<any>(null);
  const movedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast, flash, clear } = usePantryToast();
  const actions = usePantryActions(flash);
  const clearPantry = useClearPantry(flash);

  const { drag, overZone, startDrag } = usePantryDrag({
    onDrop: (item, zone) => {
      actions.moveTo(item, zone);
      setJustMovedItemId(item.id);
      if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
      movedTimerRef.current = setTimeout(() => {
        setJustMovedItemId(null);
      }, 650);
    },
    onOpen: (item) => {
      setDrawer({ t: 'item', itemId: item.id });
    },
    containerRef,
  });

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
    };
  }, []);

  // Consume pending toast on screen focus
  useFocusEffect(() => {
    const pendingMsg = consumePendingPantryToast();
    if (pendingMsg) {
      flash(pendingMsg);
    }
  });

  const importBatch = React.useMemo(
    () => activeImportBatch(snapshot.all.map((v) => v.item)),
    [snapshot.all]
  );

  // The basket is reviewed on the receipt scan's screen. Closing it without
  // confirming leaves the banner up; only the confirm marks it reviewed.
  const handleOpenImport = React.useCallback((importId: string, source: 'banner' | 'email' = 'banner') => {
    trackEvent('pantry_import_opened', { importId, source });
    setDrawer({ t: 'import', importId });
  }, []);

  useImportLink(React.useCallback((importId: string) => handleOpenImport(importId, 'email'), [handleOpenImport]));

  const handleDismissImport = (importId: string) => {
    trackEvent('pantry_import_dismissed', { importId });
    dispatch(markImportReviewedAndSync({ importId, userId }));
  };

  const handleLoadDemoSeed = async () => {
    const demoItems = buildDemoItems();
    dispatch(addItemsAndSync({ items: demoItems as any, userId }));
    trackEvent('pantry_demo_seeded');
    flash(t('pantry.add.addedCount', { count: demoItems.length }));
  };

  const handleRemoveDemoItems = async () => {
    const demoItems = snapshot.all.filter((v) => v.item.source === 'demo');
    for (const view of demoItems) {
      dispatch(removeItemAndSync({ itemId: view.item.id, userId }));
    }
    flash(t('pantry.empty.demoRemove'));
  };

  const handleItem = (itemId: string) => {
    setDrawer({ t: 'item', itemId });
  };

  // A scan needs an account (#775): a guest gets the sign-up dialog, not the camera.
  const scanGate = useScanGate('pantry_desktop', '/inventory');

  const handlePhoto = (zone: PantryLocation) => {
    scanGate.requireAccount('fridge', () =>
      router.push({
        pathname: '/(app)/inventory-camera',
        params: { analysisMode: 'fridge', zone }
      }),
    );
  };

  const handleAdd = (zone: PantryLocation) => {
    setDrawer({ t: 'add', zone });
  };

  const handleCookStrip = () => {
    setDrawer({ t: 'cook' });
  };

  const handleCookItem = (itemId: string) => {
    setDrawer({ t: 'recipes', itemId });
  };

  const handleReview = () => {
    setDrawer({ t: 'review' });
  };

  const handleReceipt = () => {
    scanGate.requireAccount('receipt', () =>
      router.push({
        pathname: '/(app)/inventory-camera',
        params: { analysisMode: 'receipt' },
      }),
    );
  };

  return (
    <View style={styles.root}>
      {snapshot.totalCount === 0 ? (
        <EmptyState
          desktop={true}
          onReceipt={handleReceipt}
          onPhotoZone={handlePhoto}
          onManual={(z) => {
            setDrawer({ t: 'add', zone: z });
          }}
          onSeed={handleLoadDemoSeed}
        />
      ) : (
        <View style={[styles.content, styles.contentContainer]}>
          {/* Header: title + meta on the left, action cluster on the right */}
          <View style={styles.header}>
            <View style={styles.headerTitleBlock}>
              <Text style={styles.title}>{t('pantry.title')}</Text>
              <View style={styles.headerMeta}>
                <Text style={styles.metaText}>{t('pantry.autoLine')} · {snapshot.totalCount}</Text>
              </View>
            </View>
            <View style={styles.headerActions}>
              {tidyCount > 0 && (
                <Pressable
                  onPress={() => setDrawer({ t: 'dedupe' })}
                  style={[
                    styles.reviewPill,
                    { backgroundColor: Paprika.surfaceLow, borderColor: Paprika.textMuted },
                  ]}
                >
                  <Text style={[styles.reviewPillText, { color: Paprika.textSecondary }]}>
                    {t('pantry.tidy.entry')} · {tidyCount}
                  </Text>
                </Pressable>
              )}
              {snapshot.reviewList.length > 0 && (
                <Pressable
                  onPress={handleReview}
                  style={[
                    styles.reviewPill,
                    { backgroundColor: Paprika.aiSoft, borderColor: Paprika.ai },
                  ]}
                >
                  <Text style={[styles.reviewPillText, { color: Paprika.ai }]}>
                    {t('pantry.review.entry')} · {snapshot.reviewList.length}
                  </Text>
                </Pressable>
              )}
              <Pressable
                onPress={handleReceipt}
                style={({ pressed }) => [
                  styles.receiptButton,
                  pressed && styles.receiptButtonPressed,
                ]}
              >
                <PantryIcon icon={{ lib: 'mci', name: 'receipt' }} size={16} color={Paprika.toastText} />
                <Text style={styles.receiptButtonText}>{t('pantry.add.receipt')}</Text>
              </Pressable>
            </View>
          </View>

          {importBatch && (
            <View style={styles.importBannerWrapper}>
              <PantryImportBanner
                batch={importBatch}
                onPress={() => handleOpenImport(importBatch.importId)}
                onDismiss={() => handleDismissImport(importBatch.importId)}
              />
            </View>
          )}

          {/* Cook strip */}
          {snapshot.cookList.length > 0 && (
            <View style={styles.cookStripWrapper}>
              <CookStrip
                count={snapshot.cookList.length}
                names={snapshot.cookList.map((v) => pantryName(v.item))}
                onPress={handleCookStrip}
              />
            </View>
          )}

          {/* Zone header line */}
          <View style={styles.zoneHeaderRow}>
            <Text style={styles.zoneHeaderTitle}>{t('pantry.arrangedLikeKitchen')}</Text>
            <Text style={styles.zoneHeaderHint}>{t('pantry.dragHint')}</Text>
          </View>

          {/* 4 zone columns */}
          <View ref={containerRef} style={[styles.columnsContainer, { position: 'relative' }]}>
            {ZONE_KEYS.map((zone) => (
              <DesktopZoneColumn
                key={zone}
                zone={zone}
                items={snapshot.byLocation[zone]}
                onItem={(itemId) => handleItem(itemId)}
                onPhoto={() => handlePhoto(zone)}
                onAdd={() => handleAdd(zone)}
                onCook={handleCookItem}
                isDragging={drag?.item.id}
                draggingOver={overZone === zone}
                justMoved={justMovedItemId}
                onItemPointerDown={startDrag}
              />
            ))}

            {/* Drag ghost card */}
            {drag && <DragGhost item={drag.item} x={drag.x} y={drag.y} />}
          </View>

          {/* Clear pantry — last on the screen, where irreversible things belong */}
          <ClearPantryRow
            count={snapshot.totalCount}
            busy={clearPantry.busy}
            onPress={clearPantry.ask}
          />
        </View>
      )}

      <ConfirmationModal
        visible={clearPantry.confirming}
        title={t('pantry.clear.confirmTitle')}
        message={t('pantry.clear.confirmBody', { count: snapshot.totalCount })}
        confirmLabel={t('pantry.clear.confirmAction')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={clearPantry.confirm}
        onCancel={clearPantry.cancel}
      />

      {/* Drawer */}
      {drawer && (
        <PantryDrawer
          visible={true}
          onClose={() => setDrawer(null)}
        >
          {drawer.t === 'item' && (
            <ItemPanel
              itemId={drawer.itemId}
              snapshot={snapshot}
              actions={actions}
              onClose={() => setDrawer(null)}
              onCook={() => {
                // This will be implemented in the ItemPanel
              }}
            />
          )}
          {drawer.t === 'add' && (
            <AddPanel
              zone={drawer.zone}
              onClose={() => setDrawer(null)}
              onAdd={(food) => {
                const category = detectCategoryFromName(food.name);
                const expiryDate = estimateExpiry(category, drawer.zone, new Date(), food.name);
                const items = [
                  {
                    name: food.name,
                    category,
                    confidence: 100,
                    quantity: 1,
                    location: drawer.zone,
                    ...(expiryDate && { expiry_date: expiryDate }),
                  },
                ];
                dispatch(addItemsAndSync({ items: items as any, userId }));
                const zoneName = t(`pantry.zones.${drawer.zone}`);
                flash(t('pantry.add.addedToZone', { name: food.name, zone: zoneName }));
                setDrawer(null);
              }}
              onPhoto={() => {
                setDrawer(null);
                handlePhoto(drawer.zone);
              }}
            />
          )}
          {drawer.t === 'cook' && (
            <CookPanel
              items={snapshot.cookList}
              onClose={() => setDrawer(null)}
              onItemPress={(itemId) => {
                setDrawer({ t: 'recipes', itemId });
              }}
              onEat={(item) => {
                actions.markEaten(item.item);
              }}
            />
          )}
          {drawer.t === 'recipes' && !!drawer.itemId && (
            <RecipesPanel
              itemId={drawer.itemId}
              item={snapshot.all.find((v) => v.item.id === drawer.itemId)?.item || null}
              onClose={() => setDrawer(null)}
            />
          )}
          {drawer.t === 'review' && (
            <ReviewPanel onClose={() => setDrawer(null)} />
          )}
          {drawer.t === 'import' && (
            <DeliveryReview
              key={drawer.importId}
              importId={drawer.importId}
              onClose={() => setDrawer(null)}
              onDone={(message) => {
                setDrawer(null);
                flash(message);
              }}
            />
          )}
          {drawer.t === 'dedupe' && (
            <DedupePanel onClose={() => setDrawer(null)} flash={flash} />
          )}
        </PantryDrawer>
      )}

      {/* Toast - desktop variant */}
      {toast && (
        <PantryToast
          toast={toast}
          onUndo={() => {
            toast.undo?.();
            clear();
          }}
          desktop={true}
        />
      )}
      {scanGate.sheet}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  header: {
    marginBottom: 24,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
  },
  headerTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  receiptButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 9,
    paddingHorizontal: 16,
    borderRadius: 999,
    backgroundColor: Paprika.accent,
  },
  receiptButtonPressed: {
    transform: [{ scale: 0.98 }],
  },
  receiptButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    fontWeight: '600',
    color: Paprika.toastText,
  },
  title: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.textPrimary,
    marginBottom: 8,
  },
  headerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  metaText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  reviewPill: {
    paddingVertical: 9,
    paddingHorizontal: 15,
    borderRadius: 999,
    borderWidth: 1,
  },
  reviewPillText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    fontWeight: '600',
  },
  importBannerWrapper: {
    marginBottom: 16,
    marginHorizontal: -16, // the banner carries its own 16px gutter
  },
  cookStripWrapper: {
    marginBottom: 24,
  },
  zoneHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  zoneHeaderTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 19,
    color: Paprika.textPrimary,
    letterSpacing: -0.01,
  },
  zoneHeaderHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
    marginLeft: 'auto',
  },
  columnsContainer: {
    flexDirection: 'row',
    gap: 16,
    flex: 1,
    // Without a min height bound, flex children in RN-web grow past the
    // viewport instead of letting the column ScrollViews take over.
    minHeight: 0,
  },
  panelPlaceholder: {
    flex: 1,
    padding: 20,
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textMuted,
  },
});
